import { StackActions } from '@react-navigation/native';
import { useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { searchObjectQuery, showSearchBar } from 'app/atoms';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import { Box } from 'app/design/primitives/box';
import { HeaderSearch } from 'app/shopify/components/layout/HeaderBar';
import { useNavigation } from 'app/hooks/navigation';
import { useSmoothVisibility } from 'app/hooks/use-smooth-visibility';
import { SearchOverlay } from 'app/screens/billboard/landing-page/components/search-overlay';
import { navigateToAllListings } from 'app/screens/billboard/navigator-helper';
import { useTheme } from 'app/shopify';
import { ListingType } from 'app/types/listing';
import { TutorialRef } from 'app/types/tutorial';
import { useBreakpoint, useIsGuestWeb } from 'app/utils/web-helpers';

export function HeaderSearchBar({ fullWidth = false }: { fullWidth?: boolean }) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useAtom(searchObjectQuery);
  const navigation = useNavigation();
  const { viewRef: headerSearchBarRef } = useTutorialRef(TutorialRef.HeaderSearchBar);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [localQueryState, setLocalQueryState] = useState({ source: searchQuery, draft: searchQuery });
  const { t: tHeader } = useTranslation('common');

  let localQuery = localQueryState.draft;
  if (localQueryState.source !== searchQuery) {
    localQuery = searchQuery;
    setLocalQueryState({ source: searchQuery, draft: localQuery });
  }
  const setLocalQuery = (draft: string) => setLocalQueryState({ source: searchQuery, draft });

  const getCurrentListingsTabParams = () => {
    const state = navigation.getState();
    const currentRoute = state.routes[state.index];

    if (currentRoute?.name !== 'all-listings') {
      return { activeTab: ListingType.offer, type: ListingType.offer };
    }

    const params = currentRoute.params as { activeTab?: ListingType | 'people'; type?: ListingType } | undefined;
    const type = params?.type ?? ListingType.offer;

    return {
      activeTab: params?.activeTab ?? type,
      type,
    };
  };

  const onSubmit = (query: string) => {
    setIsOverlayVisible(false);
    setLocalQueryState({ source: query, draft: query });
    setSearchQuery(query);

    const state = navigation.getState();
    const { activeTab, type } = getCurrentListingsTabParams();

    if (state.index > 0) {
      navigation.dispatch(StackActions.popToTop());
    }

    navigateToAllListings(navigation, {
      activeTab,
      type,
      search: query || undefined,
    });
  };

  const isOverBreakpoint = useBreakpoint(1024);
  const isGuestWeb = useIsGuestWeb();
  const showSearchBarValue = useAtomValue(showSearchBar);
  const navigationState = navigation.getState();
  const currentRoute = navigationState.routes[navigationState.index];
  const isAllListingsRoute = currentRoute?.name === 'all-listings';

  const isVisible = fullWidth || isGuestWeb || (isOverBreakpoint && (!isAllListingsRoute || showSearchBarValue));

  const { animatedStyle } = useSmoothVisibility(isVisible);

  if (!isVisible) return null;

  const wrapperStyle = fullWidth
    ? { width: '100%' as const, height: 48, minWidth: 0 }
    : {
        flex: 1,
        minWidth: 0,
        marginLeft: theme.spacing['spacing.general.x6'],
        marginRight: theme.spacing['spacing.general.x6'],
      };

  return (
    <Animated.View style={[wrapperStyle, animatedStyle]}>
      <Box position="relative" width="100%" zIndex={20}>
        {isOverlayVisible ? (
          <Pressable
            onPress={() => setIsOverlayVisible(false)}
            style={{
              ...StyleSheet.absoluteFill,
              position: 'fixed' as 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
            }}
          />
        ) : null}

        <Box ref={headerSearchBarRef} collapsable={false} position="relative" zIndex={2}>
          <HeaderSearch
            value={localQuery}
            onChangeText={setLocalQuery}
            onFocus={() => setIsOverlayVisible(true)}
            onSubmit={(query) => onSubmit(query.trim())}
            placeholder={tHeader('searchPlaceholder')}
            accessibilityLabel={tHeader('searchPlaceholder')}
            submitLabel={tHeader('search')}
            clearLabel={tHeader('search.clearSearch', { ns: 'accessibility' })}
          />
        </Box>

        {isOverlayVisible ? (
          <Box position="absolute" top="100%" left={0} right={0} marginTop="spacing.general.x2" zIndex={3}>
            <SearchOverlay
              initialValue={searchQuery}
              onClose={() => setIsOverlayVisible(false)}
              onSubmit={onSubmit}
              onCategoryPress={(categoryId) => {
                setIsOverlayVisible(false);
                navigateToAllListings(navigation, {
                  activeTab: ListingType.offer,
                  categoryId,
                  search: '',
                  tagId: undefined,
                  type: ListingType.offer,
                });
              }}
              onTagPress={(tagId) => {
                setIsOverlayVisible(false);
                navigateToAllListings(navigation, {
                  activeTab: ListingType.offer,
                  tagId,
                  type: ListingType.offer,
                });
              }}
              onViewAllListings={() => {
                setIsOverlayVisible(false);
                navigateToAllListings(navigation, {
                  activeTab: ListingType.offer,
                  type: ListingType.offer,
                });
              }}
              variant="dropdown"
              withSearchInput={false}
            />
          </Box>
        ) : null}
      </Box>
    </Animated.View>
  );
}
